/**
 * Manage Edges Modal
 * Simple interface for assigning/removing edge devices to/from locations
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner, DeleteConfirmDialog } from '@/components/ui';
import { AlertCircle, Server, Plus, X } from 'lucide-react';
import type { Location, CentralEdgeDevice } from '@simcheck/shared-types';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { edgeDeviceService } from '../../services/edgeDeviceService';
import { db } from '@/config/firebase';
import { doc, updateDoc, increment } from 'firebase/firestore';

interface AssignModemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  location: Location;
}

export const AssignModemModal: React.FC<AssignModemModalProps> = ({ isOpen, onClose, onSuccess, location }) => {
  const { success, error: showError } = useToast();
  const { isSuperAdmin } = useAuth();
  const [assignedEdges, setAssignedEdges] = useState<CentralEdgeDevice[]>([]);
  const [availableEdges, setAvailableEdges] = useState<CentralEdgeDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingEdgeId, setProcessingEdgeId] = useState<string | null>(null);
  // Edge device waiting for confirmation in the DeleteConfirmDialog before it is removed
  const [pendingRemoveEdge, setPendingRemoveEdge] = useState<CentralEdgeDevice | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadEdgeDevices();
    }
  }, [isOpen, location.id]);

  const loadEdgeDevices = async () => {
    setLoading(true);
    setError(null);
    
    // Platform admin only - tenant users should never see edge devices
    if (!isSuperAdmin) {
      setError('Access denied. Edge device management is restricted to platform administrators only.');
      setLoading(false);
      return;
    }
    
    try {
      // Fetch all edge devices from API
      const allEdges = await edgeDeviceService.getDevices();

      // Split into assigned (to this location) and available (unassigned or no location)
      const assigned = allEdges.filter(edge => edge.locationId === location.id);
      const available = allEdges.filter(edge => !edge.locationId || edge.locationId === '');
      
      setAssignedEdges(assigned);
      setAvailableEdges(available);
    } catch (err) {
      console.error('Failed to load edge devices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load edge devices.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignEdge = async (edgeId: string) => {
    if (!isSuperAdmin) {
      showError('Access denied. Only platform administrators can manage edge devices.');
      return;
    }
    
    setProcessingEdgeId(edgeId);
    setError(null);
    try {
      // Get the edge device to find out how many modems it has
      const edgeToAssign = availableEdges.find(e => e.id === edgeId);
      const modemCount = edgeToAssign?.modems?.length || 0;
      
      // Assign edge device to this location via API with full location metadata
      await edgeDeviceService.assignToLocation(
        edgeId, 
        location.id,
        location.displayName || location.name,
        location.countryId,
        location.regionId
      );

      // Update location's modem counts in Firestore
      if (modemCount > 0) {
        const locationRef = doc(db, 'locations', location.id);
        await updateDoc(locationRef, {
          deviceCount: increment(1),
          modemCount: increment(modemCount),
          availableModemCount: increment(modemCount), // All modems from newly assigned device are available
          onlineDeviceCount: increment(edgeToAssign?.status === 'online' ? 1 : 0)
        });
      }

      success('Edge device assigned successfully');
      
      // Refresh the lists
      await loadEdgeDevices();
      onSuccess();
    } catch (err) {
      console.error('Failed to assign edge device:', err);
      showError(err instanceof Error ? err.message : 'Failed to assign edge device');
    } finally {
      setProcessingEdgeId(null);
    }
  };

  const handleRemoveEdge = async (edgeId: string) => {
    if (!isSuperAdmin) {
      showError('Access denied. Only platform administrators can manage edge devices.');
      return;
    }
    
    setProcessingEdgeId(edgeId);
    setError(null);
    try {
      // Get the edge device to find out how many modems it has
      const edgeToRemove = assignedEdges.find(e => e.id === edgeId);
      const modemCount = edgeToRemove?.modems?.length || 0;
      
      // Remove edge device from location via API
      await edgeDeviceService.removeFromLocation(edgeId);

      // Update location's modem counts in Firestore
      if (modemCount > 0) {
        const locationRef = doc(db, 'locations', location.id);
        await updateDoc(locationRef, {
          deviceCount: increment(-1),
          modemCount: increment(-modemCount),
          availableModemCount: increment(-modemCount),
          onlineDeviceCount: increment(edgeToRemove?.status === 'online' ? -1 : 0)
        });
      }

      success('Edge device removed from location');
      
      // Refresh the lists
      await loadEdgeDevices();
      onSuccess();
    } catch (err) {
      console.error('Failed to remove edge device:', err);
      showError(err instanceof Error ? err.message : 'Failed to remove edge device');
    } finally {
      setProcessingEdgeId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Edge Devices"
      description={`Assign or remove edge devices for ${location.displayName || location.name}`}
    >
      <div className="space-y-6 py-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <div>{error}</div>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* Currently Assigned Edges */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Currently Assigned ({assignedEdges.length})
              </h3>
              {assignedEdges.length === 0 ? (
                <div className="p-4 bg-muted rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground text-center">
                    No edge devices assigned to this location
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {assignedEdges.map((edge) => (
                    <div
                      key={edge.id}
                      className="flex items-center justify-between p-3 bg-card rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <Server className="w-4 h-4 text-primary-500" />
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {edge.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {edge.modems?.length || 0} modems • {edge.status}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingRemoveEdge(edge)}
                        disabled={processingEdgeId === edge.id}
                      >
                        {processingEdgeId === edge.id ? <Spinner size="sm" /> : <X className="mr-2 h-4 w-4" />}
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Edges */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Available to Assign ({availableEdges.length})
              </h3>
              {availableEdges.length === 0 ? (
                <div className="p-4 bg-muted rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground text-center">
                    No unassigned edge devices available
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableEdges.map((edge) => (
                    <div
                      key={edge.id}
                      className="flex items-center justify-between p-3 bg-card rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <Server className="w-4 h-4 text-muted-foreground/70" />
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {edge.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {edge.modems?.length || 0} modems • {edge.status}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAssignEdge(edge.id)}
                        disabled={processingEdgeId === edge.id}
                      >
                        {processingEdgeId === edge.id ? <Spinner size="sm" /> : <Plus className="mr-2 h-4 w-4" />}
                        Assign
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      <div className="flex justify-end mt-6">
        <Button variant="default" onClick={onClose}>
          Done
        </Button>
      </div>

      <DeleteConfirmDialog
        open={pendingRemoveEdge !== null}
        onClose={() => setPendingRemoveEdge(null)}
        title="Remove edge device?"
        verb="remove"
        confirmText="Remove"
        itemName={pendingRemoveEdge?.name}
        details={pendingRemoveEdge
          ? `It is unassigned from ${location.name}, and its ${pendingRemoveEdge.modems?.length || 0} modem(s) stop taking tests here.`
          : undefined}
        onConfirm={async () => {
          if (pendingRemoveEdge) await handleRemoveEdge(pendingRemoveEdge.id);
        }}
      />
    </Modal>
  );
};

